/**
 * Shared time parsing and formatting utilities.
 * Replaces 8+ duplicate implementations across web, mobile, and shared internals.
 */

/**
 * Parse a time string to minutes since midnight.
 * Handles: "14:30", "2:30 PM", "5pm", "10am", "08:00"
 * @param {string} timeStr
 * @returns {number} minutes since midnight (0-1439)
 */
export function parseTimeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const str = timeStr.toString().trim();

  // Handle 12-hour with minutes: "2:30 PM", "11:00 AM"
  const ampmFull = str.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (ampmFull) {
    let h = parseInt(ampmFull[1], 10);
    const m = parseInt(ampmFull[2], 10);
    if (ampmFull[3].toUpperCase() === 'PM' && h !== 12) h += 12;
    if (ampmFull[3].toUpperCase() === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  }

  // Handle short 12-hour: "5pm", "10am"
  const ampmShort = str.match(/^(\d{1,2})\s*(AM|PM)$/i);
  if (ampmShort) {
    let h = parseInt(ampmShort[1], 10);
    if (ampmShort[2].toUpperCase() === 'PM' && h !== 12) h += 12;
    if (ampmShort[2].toUpperCase() === 'AM' && h === 12) h = 0;
    return h * 60;
  }

  // Handle 24-hour: "14:30", "08:00"
  const parts = str.split(':');
  if (parts.length >= 2) {
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
  }

  return 0;
}

/**
 * Convert a time value to 12-hour display format.
 * Accepts minutes (number) or time string.
 * @param {number|string} timeInput - minutes since midnight or time string
 * @param {{ dayOffset?: number }} opts - optional: append "+1d" if dayOffset > 0
 * @returns {string} e.g. "2:30 PM", "8:00 AM", "11:30 PM (+1d)"
 */
export function formatTime12(timeInput, opts = {}) {
  if (!timeInput && timeInput !== 0) return '';
  const rawMins = typeof timeInput === 'number' ? timeInput : parseTimeToMinutes(timeInput);
  // Auto-detect day rollover: if the caller passed a value >= 1440 min (or a
  // string like "24:10") without an explicit dayOffset, infer it so we never
  // silently render "12:10 AM" on the wrong day bucket.
  const inferredDayOffset = rawMins >= 0 ? Math.floor(rawMins / 1440) : 0;
  const mins = ((rawMins % 1440) + 1440) % 1440;
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  let result = `${h12}:${m.toString().padStart(2, '0')} ${suffix}`;
  const explicitDayOffset = (opts.dayOffset && opts.dayOffset > 0) ? opts.dayOffset : 0;
  const effectiveDayOffset = Math.max(explicitDayOffset, inferredDayOffset);
  if (effectiveDayOffset > 0) {
    result += ` (+${effectiveDayOffset}d)`;
  }
  return result;
}

/**
 * Convert total minutes to 24-hour "HH:MM" format.
 * @param {number} totalMinutes
 * @returns {string} e.g. "14:30", "08:00"
 */
export function minutesToTime(totalMinutes, returnObject = false) {
  // Handle negative minutes (safety)
  const normalizedMinutes = totalMinutes < 0 ? totalMinutes + 1440 : totalMinutes;
  const dayOffset = Math.floor(normalizedMinutes / 1440);
  const minutesInDay = normalizedMinutes % 1440;
  const h = Math.floor(minutesInDay / 60);
  const m = minutesInDay % 60;
  const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;

  if (returnObject) {
    return { time: timeStr, dayOffset };
  }
  return timeStr;
}

/**
 * Calculate end time from start time + duration, handling midnight crossing.
 * Duration can be "2 hours", "1.5 hours", "90 minutes", "2h 30m", or a number (minutes).
 *
 * @param {string} startTime - Start time string (e.g., "14:30")
 * @param {string|number} duration - Duration string or minutes
 * @returns {{ endTime: string, endTimeFormatted: string, crossesMidnight: boolean, dayOffset: number, totalMinutes: number }}
 */
export function calculateEndTime(startTime, duration) {
  const startMinutes = parseTimeToMinutes(startTime);
  let durationMinutes = 0;

  if (typeof duration === 'number') {
    durationMinutes = duration;
  } else if (typeof duration === 'string') {
    const hoursMatch = duration.match(/(\d+(?:\.\d+)?)\s*h(?:ours?)?/i);
    const minsMatch = duration.match(/(\d+)\s*m(?:in(?:utes?)?)?/i);
    const justMinsMatch = duration.match(/^(\d+)\s*minutes?$/i);

    if (hoursMatch) durationMinutes += parseFloat(hoursMatch[1]) * 60;
    if (minsMatch) durationMinutes += parseInt(minsMatch[1]);
    if (justMinsMatch && !hoursMatch) durationMinutes = parseInt(justMinsMatch[1]);

    // Fallback: if just a number, assume hours
    if (durationMinutes === 0) {
      const numMatch = duration.match(/(\d+(?:\.\d+)?)/);
      if (numMatch) durationMinutes = parseFloat(numMatch[1]) * 60;
    }
  }

  const endMinutes = startMinutes + durationMinutes;
  const result = minutesToTime(endMinutes, true);

  return {
    endTime: result.time,
    endTimeFormatted: formatTime12(result.time, { dayOffset: result.dayOffset }),
    crossesMidnight: result.dayOffset > 0,
    dayOffset: result.dayOffset,
    totalMinutes: endMinutes,
  };
}

/**
 * Check if a time range crosses midnight.
 * @param {string} startTime - Start time string
 * @param {string} endTime - End time string
 * @returns {boolean}
 */
export function doesCrossMidnight(startTime, endTime) {
  return parseTimeToMinutes(endTime) < parseTimeToMinutes(startTime);
}

/**
 * Calculate minutes between two times, accounting for midnight crossing.
 * @param {string} startTime
 * @param {string} endTime
 * @param {boolean} [assumesCrossingMidnight=false]
 * @returns {number}
 */
export function minutesBetween(startTime, endTime, assumesCrossingMidnight = false) {
  const startMins = parseTimeToMinutes(startTime);
  const endMins = parseTimeToMinutes(endTime);
  if (assumesCrossingMidnight || endMins < startMins) {
    return (1440 - startMins) + endMins;
  }
  return endMins - startMins;
}

/**
 * Find transit data with fuzzy key matching.
 * Handles variations in location string formatting.
 *
 * @param {Object} transitData - Transit data object keyed by "from-to" strings
 * @param {string} fromLocation - Origin location
 * @param {string} toLocation - Destination location
 * @returns {Object|null} Transit data entry or null
 */
export function findTransitData(transitData, fromLocation, toLocation) {
  if (!transitData || !fromLocation || !toLocation) return null;

  const normalize = (s) => (s || '').toLowerCase().trim()
    .replace(/^the\s+/i, '')
    .replace(/,.*$/, '')
    .replace(/\s+/g, ' ');

  const fromNorm = normalize(fromLocation);
  const toNorm = normalize(toLocation);

  // Try exact key first
  const exactKey = `${fromLocation}-${toLocation}`;
  if (transitData[exactKey]) return transitData[exactKey];

  // Try all stored keys and find best match
  for (const key of Object.keys(transitData)) {
    const lastDashIdx = key.lastIndexOf('-');
    if (lastDashIdx < 0) continue;
    const storedFrom = key.substring(0, lastDashIdx);
    const storedTo = key.substring(lastDashIdx + 1);
    const storedFromNorm = normalize(storedFrom);
    const storedToNorm = normalize(storedTo);

    if (storedFromNorm === fromNorm && storedToNorm === toNorm) return transitData[key];
    if ((storedFromNorm.includes(fromNorm) || fromNorm.includes(storedFromNorm)) &&
        (storedToNorm.includes(toNorm) || toNorm.includes(storedToNorm))) {
      return transitData[key];
    }
  }

  return null;
}
